import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ExternalLink, RefreshCw, Search, AlertCircle, DollarSign } from 'lucide-react';

const PAYMENT_BADGE = (status) => {
  if (status === 'past_due' || status === 'unpaid') {
    return <Badge variant="outline" className="bg-amber-100 text-amber-800">Inadimplente</Badge>;
  }
  if (status === 'canceled') {
    return <Badge variant="outline" className="bg-gray-200 text-gray-700">Cancelado</Badge>;
  }
  if (status === 'active' || status === 'trialing') {
    return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Ativo</Badge>;
  }
  return <Badge variant="outline" className="bg-slate-100 text-slate-700">—</Badge>;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return '—'; }
};

const planLabel = (p) => ({ starter: 'Starter (gratuito)', solo: 'Solo (R$ 129/mês)', clinica: 'Clínica (R$ 99/assento)' }[p] || (p || 'Starter'));

export default function AdminBilling() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await api.admin.getBilling();
      setRows(data.subscriptions || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar dados de assinatura');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.email || '').toLowerCase().includes(q) || (r.full_name || '').toLowerCase().includes(q));
  }, [rows, query]);

  const totals = useMemo(() => {
    const counts = { active: 0, past_due: 0, canceled: 0, none: 0 };
    rows.forEach(r => {
      const s = r.subscription_status;
      if (s === 'active' || s === 'trialing') counts.active++;
      else if (s === 'past_due' || s === 'unpaid') counts.past_due++;
      else if (s === 'canceled') counts.canceled++;
      else counts.none++;
    });
    return counts;
  }, [rows]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-billing-title">Assinaturas</h1>
            <p className="text-gray-500">Status de pagamento, próxima cobrança e link Stripe</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="admin-billing-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Ativos</p><p className="text-2xl font-bold text-emerald-600">{totals.active}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Inadimplentes</p><p className="text-2xl font-bold text-amber-600">{totals.past_due}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Cancelados</p><p className="text-2xl font-bold text-gray-600">{totals.canceled}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Starter (gratuito)</p><p className="text-2xl font-bold text-blue-600">{totals.none}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-500" /> Lista de Assinaturas
            </CardTitle>
            <CardDescription>{rows.length} usuários · clique em "Stripe" para abrir o cliente no painel</CardDescription>
            <div className="relative mt-3 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome ou email..."
                className="pl-10"
                data-testid="admin-billing-search"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-2" />
                <p className="text-red-500">{error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-billing-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Usuário</th>
                      <th className="text-left py-3 px-2">Plano atual</th>
                      <th className="text-left py-3 px-2">Status pagamento</th>
                      <th className="text-left py-3 px-2">Próxima cobrança</th>
                      <th className="text-right py-3 px-2">Stripe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.user_id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`billing-row-${r.user_id}`}>
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium">{r.full_name || '—'}</p>
                            <p className="text-xs text-gray-500">{r.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2">{planLabel(r.plan_type)}</td>
                        <td className="py-3 px-2">{PAYMENT_BADGE(r.subscription_status)}</td>
                        <td className="py-3 px-2 text-xs">{fmtDate(r.current_period_end)}</td>
                        <td className="py-3 px-2 text-right">
                          {r.stripe_customer_id ? (
                            <a
                              href={`https://dashboard.stripe.com/customers/${r.stripe_customer_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-purple-600 hover:underline"
                              data-testid={`stripe-link-${r.user_id}`}
                            >
                              Stripe <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Nenhum resultado.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
